import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoPopover from "../../Components/Common/poper/InfoPopover";
import DataTable from "../../Components/Common/datatable/DataTable";
import { useEffect, useRef, useState } from "react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
  safeTimestamp,
  Timestamp,
} from "../../Components/Common/DateFormatter";
import ButtonComponent from "../../Components/Common/button/Button";
import { useCallback } from "react";
import { useSelector } from "react-redux";
import SaveAltOutlinedIcon from "@mui/icons-material/SaveAltOutlined";
import ApiService from "../../utils/fetch";
import { MessagePopup } from "../../Components/Common/messagePopup/MessagePopup";
import SaveIcon from "@mui/icons-material/Save";
import { useMemo } from "react";

// Place this function at the top (outside your component)
function getResponsiveTableHeight() {
  const h = window.innerHeight;
  if (h <= 720) return 400; // For 720p screens
  if (h <= 1080) return 600; // For 1080p screens
  if (h <= 1440) return 800; // For 1440p screens
  if (h <= 2160) return 1200; // For 2160p screens (4K)
  return 1400; // For anything larger
}
function ProfitCenterSelection() {
  const gridRef = useRef(null);
  const [tableHeight, setTableHeight] = useState(getResponsiveTableHeight());

  const [filteredData, setFilteredData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectedRow, setSelectedRow] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [remarks, setRemarks] = useState("");
  const [pendingParams, setPendingParams] = useState(null);

  const user = useSelector((state) => state.user.userData);

  useEffect(() => {
    function handleResize() {
      setTableHeight(getResponsiveTableHeight());
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    postData();
  }, []);

  const postData = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get("/v1/get-profit-centers");
      setFilteredData(response.data);
    } catch (error) {
      console.error("Error posting data", error);
    } finally {
      setLoading(false);
    }
  };

  function dateComparator(date1, date2) {
    // Convert both ISO 8601 strings to timestamps (milliseconds since Unix Epoch)
    const date1Number = _isoDateToTimestamp(date1);
    const date2Number = _isoDateToTimestamp(date2);

    // Handle null or invalid dates
    if (date1Number === null && date2Number === null) {
      return 0;
    }
    if (date1Number === null) {
      return -1;
    }
    if (date2Number === null) {
      return 1;
    }

    // Compare timestamps
    return date1Number - date2Number;
  }

  function _isoDateToTimestamp(isoDate) {
    if (!isoDate || typeof isoDate !== "string") {
      return null;
    }

    const date = new Date(isoDate);
    // Check if Date parsing failed
    if (isNaN(date.getTime())) {
      return null;
    }

    return date.getTime();
  }

  const columnDefinition = useMemo(
    () => [
      {
        field: "region",
        headerName: "Region",
        sortable: true,
        unSortIcon: true,
        editable: false,
        cellRenderer: (params) => (
          <Tooltip title={`${params.data.region}`} arrow>
            <div>{params.data.region}</div>
          </Tooltip>
        ),
        width: 200,
        minWidth: 200,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => {
            params.api.hidePopupMenu();
          },
          onReset: (params) => {
            params.api.hidePopupMenu();
          },
        },
        filter: "agTextColumnFilter",
      },
      {
        field: "customerName",
        headerName: "Forecast Partner",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,
        valueGetter: (params) => {
          const { customerId, customerName, customerCountry } = params.data;
          return `${customerId || ""} - ${customerName || ""} - ${
            customerCountry || ""
          }`;
        },
        cellRenderer: (params) => {
          const display = params.value;
          return (
            <Tooltip title={<span>{display}</span>} arrow>
              <div>{display}</div>
            </Tooltip>
          );
        },
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => {
            params.api.hidePopupMenu();
          },
          onReset: (params) => {
            params.api.hidePopupMenu();
          },
        },
        filter: "agTextColumnFilter",
      },
      {
        field: "locationName",
        headerName: "Plant",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,
        valueGetter: (params) => {
          const { locationId, locationName, locationCountry } = params.data;
          return `${locationId || ""} - ${locationName || ""} - ${
            locationCountry || ""
          }`;
        },
        cellRenderer: (params) => {
          const display = params.value;
          return (
            <Tooltip title={<span>{display}</span>} arrow>
              <div>{display}</div>
            </Tooltip>
          );
        },
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => {
            params.api.hidePopupMenu();
          },
          onReset: (params) => {
            params.api.hidePopupMenu();
          },
        },
        filter: "agTextColumnFilter",
      },
      {
        field: "productDescription",
        headerName: "Product",
        sortable: true,
        unSortIcon: true,
        width: 200,
        minWidth: 200,
        valueGetter: (params) => {
          const { productId, productDescription } = params.data;
          return `${productId || ""} - ${productDescription || ""}`;
        },
        cellRenderer: (params) => {
          const display = params.value;
          return (
            <Tooltip title={<span>{display}</span>} arrow>
              <div>{display}</div>
            </Tooltip>
          );
        },
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => {
            params.api.hidePopupMenu();
          },
          onReset: (params) => {
            params.api.hidePopupMenu();
          },
        },
        filter: "agTextColumnFilter",
      },
      {
        field: "activeProfitCenter",
        headerName: "Profit Center",
        editable: true,
        resizable: false,
        cellEditor: "agSelectCellEditor",
        cellClass: "red-border",
        cellEditorParams: (params) => ({
          values: params.data.profitCenterOptions || [],
        }),
        cellRenderer: (params) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              //color: "red",
            }}
          >
            <span>{params.value}</span>
            <ArrowDropDownIcon style={{ fontSize: 25 }} />
          </div>
        ),
        sortable: true,
        unSortIcon: true,
        width: 240,
        minWidth: 240,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => params.api.hidePopupMenu(),
          onReset: (params) => params.api.hidePopupMenu(),
        },
        filter: "agTextColumnFilter",
      },
      {
        field: "",
        headerName: "",
        width: 90,
        minWidth: 90,
        cellRenderer: (params) => (
          <SaveIcon
            style={{
              cursor: params.data.isEdited ? "pointer" : "not-allowed",
              color: params.data.isEdited ? "#000" : "#ccc",
              opacity: params.data.isEdited ? 1 : 0.5,
              pointerEvents: params.data.isEdited ? "auto" : "none",
              marginTop: "7px",
            }}
            onClick={() => {
              if (params.data.isEdited) {
                setPendingParams(params);
                setRemarks(""); // reset remarks
                setOpenDialog(true);
              }
            }}
          />
        ),
      },
      {
        field: "createdAt",
        headerName: "Created Date UTC",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,
        filterParams: {
          comparator: function (filterDate, cellValue) {
            if (!cellValue) return -1;

            const cellDate = new Date(cellValue);
            // Strip time and compare only YYYY-MM-DD
            const cellDateOnly = new Date(
              cellDate.getFullYear(),
              cellDate.getMonth(),
              cellDate.getDate()
            );

            return cellDateOnly.getTime() - filterDate.getTime();
          },
          filterOptions: ["equals", "inRange"],
          defaultOption: "equals",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => params.api.hidePopupMenu(),
          onReset: (params) => params.api.hidePopupMenu(),
        },
        filter: "agDateColumnFilter",
        comparator: dateComparator,
        sort: "desc",
        valueFormatter: (params) => {
          return safeTimestamp(params.value);
        },
      },
      {
        field: "modifiedTimestampUTC",
        headerName: "Last Updated UTC",
        sortable: true,
        unSortIcon: true,
        width: 250,
        minWidth: 250,
        filterParams: {
          comparator: function (filterDate, cellValue) {
            if (!cellValue) return -1;

            const cellDate = new Date(cellValue);
            // Strip time and compare only YYYY-MM-DD
            const cellDateOnly = new Date(
              cellDate.getFullYear(),
              cellDate.getMonth(),
              cellDate.getDate()
            );

            return cellDateOnly.getTime() - filterDate.getTime();
          },
          filterOptions: ["equals", "inRange"],
          defaultOption: "equals",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => {
            params.api.hidePopupMenu();
          },
          onReset: (params) => {
            params.api.hidePopupMenu();
          },
        },
        filter: "agDateColumnFilter",
        valueFormatter: (params) => {
          return safeTimestamp(params.value);
        },
      },
      {
        field: "modifiedBy",
        headerName: "Last Updated By",
        sortable: true,
        unSortIcon: true,
        cellRenderer: (params) => <div>{`${params.data.modifiedBy}`}</div>,
        width: 240,
        minWidth: 240,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => {
            params.api.hidePopupMenu();
          },
          onReset: (params) => {
            params.api.hidePopupMenu();
          },
        },
        filter: "agTextColumnFilter",
      },
      {
        field: "comments",
        headerName: "Remarks",
        sortable: true,
        unSortIcon: true,
        editable: false,
        cellRenderer: (params) => (
          <Tooltip title={`${params.data.comments}`} arrow>
            <div>{params.data.comments}</div>
          </Tooltip>
        ),
        width: 250,
        minWidth: 250,
        filterParams: {
          filterOptions: ["contains", "startsWith", "endsWith"],
          defaultOption: "contains",
          buttons: ["apply", "reset"],
          closeOnApply: true,
          onApply: (params) => {
            params.api.hidePopupMenu();
          },
          onReset: (params) => {
            params.api.hidePopupMenu();
          },
        },
        filter: "agTextColumnFilter",
      },
    ],
    []
  );

  const getProfitCenterNumber = (value) => {
    if (!value) return "";
    return value.split("-")[0].trim();
  };

  const save = async (params) => {
    const requestBody = {
      id: params.data.id,
      customerId: params.data.customerId,
      customerName: params.data.customerName,
      customerCountry: params.data.customerCountry,
      locationId: params.data.locationId,
      locationName: params.data.locationName,
      locationCountry: params.data.locationCountry,
      oldActiveProfitCenter: getProfitCenterNumber(
        params.data.oldActiveProfitCenter
      ),
      newActiveProfitCenter: getProfitCenterNumber(
        params.data.newActiveProfitCenter
      ),
      productId: params.data.productId || "",
      comments: remarks,
      modifiedBy: params.data.modifiedBy || "",
      modifiedTimestampUTC: new Date().toISOString(),
    };

    try {
      await ApiService.post(`/v1//save-profit-center`, requestBody);

      MessagePopup({
        icon: "success",
        title: "Profit Center Saved",
        message: "Profit Center updated successfully.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
      postData();
    } catch (error) {
      console.error("Error deleting user:", error);
      MessagePopup({
        icon: "error",
        title: "Saved Failed",
        message: "Failed to save the Profit Center. Please try again.",
        confirmButtonText: "Ok",
        confirmButtonColor: "#000000",
      });
    }
  };

  const pagination = true;
  const paginationPageSize = 10;
  const paginationPageSizeSelector = [5, 10, 20, 50, 100];

  const onGridExportCSV = useCallback(() => {
    if (gridRef.current && gridRef.current.api) {
      // Find all columns with field "activeProfitCenter"
      const activeProfitCenterColumns = columnDefinition.filter(
        (col) => col.field === "activeProfitCenter"
      );
      // Exclude fields for export
      const excludeFields = ["profitCenterOptions", "save"];
      if (activeProfitCenterColumns.length > 1) {
        // Exclude the second (editable) "activeProfitCenter" column
        const secondHeader = activeProfitCenterColumns[1].headerName;
        if (secondHeader === "Select Profit Center") {
          excludeFields.push("Select Profit Center");
        }
      }

      gridRef.current.api.exportDataAsCsv({
        fileName: `ProfitCenter_${new Date().toISOString()}.csv`,
        processHeaderCallback: (params) => {
          // Exclude by field or by headerName
          if (
            excludeFields.includes(params.column.getColId()) ||
            params.column.getColDef().headerName === "Select Profit Center"
          ) {
            return null;
          }
          const column = columnDefinition.find(
            (col) =>
              col.field === params.column.getColId() &&
              col.headerName === params.column.getColDef().headerName
          );
          return column ? column.headerName : params.column.getColId();
        },
        processCellCallback: (params) => {
          if (
            excludeFields.includes(params.column.getColId()) ||
            params.column.getColDef().headerName === "Select Profit Center"
          ) {
            return null;
          }
          // Forecast Partner (customerName column)
          if (params.column.getColId() === "customerName") {
            const row = params.node && params.node.data ? params.node.data : {};
            const { customerId, customerName, customerCountry } = row;
            return `${customerId || ""} - ${customerName || ""} - ${
              customerCountry || ""
            }`;
          }
          // Plant (locationName column)
          if (params.column.getColId() === "locationName") {
            const row = params.node && params.node.data ? params.node.data : {};
            const { locationId, locationName, locationCountry } = row;
            return `${locationId || ""} - ${locationName || ""} - ${
              locationCountry || ""
            }`;
          }
          // Product (productDescription column)
          if (params.column.getColId() === "productDescription") {
            const row = params.node && params.node.data ? params.node.data : {};
            const { productId, productDescription } = row;
            return `${productId || ""} - ${productDescription || ""}`;
          }
          // Use your Timestamp formatter for the exported value
          if (params.column.getColId() === "modifiedTimestampUTC") {
            return safeTimestamp(params.value);
          }
          if (params.column.getColId() === "createdAt") {
            return safeTimestamp(params.value);
          }
          return params.value;
        },
        useValueFormatterForExport: false,
        allColumns: true,
        onlySelected: false,
        suppressQuotes: false,
        columnKeys: columnDefinition
          .filter(
            (col) =>
              !["profitCenterOptions", "save"].includes(col.field) &&
              !(
                col.field === "activeProfitCenter" &&
                col.headerName === "Select Profit Center"
              )
          )
          .map((col) => col.field),
      });
    } else {
      console.error("gridRef or gridRef.api is not available");
    }
  }, [gridRef, columnDefinition]);

  const onCellValueChanged = (params) => {
    if (params.colDef.field === "activeProfitCenter") {
      const updatedRow = {
        ...params.data,
        oldActiveProfitCenter: params.oldValue,
        newActiveProfitCenter: params.newValue,
        isEdited: params.oldValue !== params.newValue,
      };
      setFilteredData((prev) =>
        prev.map((row) => (row.id === updatedRow.id ? updatedRow : row))
      );
    }
  };

  const getRowStyle = (params) => {
    if (params.data.isEdited) {
      return {
        border: "1.5px solid red",
        borderRadius: "4px",
      };
    }
    return {};
  };

  const onGrid = (params) => {
    const gridApi = params.api;
    if (gridApi) {
      gridApi.sizeColumnsToFit();
    } else {
      console.error("Grid API is not available.");
    }
  };

  useEffect(() => {
    function handleResize() {
      if (gridRef.current && gridRef.current.api) {
        gridRef.current.api.sizeColumnsToFit();
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  return (
    <>
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Enter Remarks for Changing Profit Center</DialogTitle>
        <DialogContent>
          <TextField
            label="Remarks"
            value={remarks}
            onChange={(e) => {
              if (e.target.value.length <= 100) setRemarks(e.target.value);
            }}
            inputProps={{ maxLength: 100 }}
            placeholder="Enter remarks (max 100 chars)"
            fullWidth
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenDialog(false)}
            sx={{
              backgroundColor: "#808080",
              color: "#fff",
              "&:hover": { backgroundColor: "#666" },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              await save(pendingParams, remarks);
              setOpenDialog(false);
            }}
            disabled={remarks.trim().length === 0}
            variant="contained"
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              "&:hover": { backgroundColor: "#222" },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      <div>
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "10px",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Typography variant="subtitle1" align="left" gutterBottom>
                Profit Center Selection
              </Typography>
              <InfoPopover margin="0 0 3px 0" />
            </Box>
            <ButtonComponent
              label="Download"
              bgColor="black"
              color="white"
              onClick={onGridExportCSV}
            />
          </Box>
          {loading ? (
            <Skeleton
              variant="rectangular"
              height={tableHeight}
              animation="pulse"
              style={{ marginBottom: "16px", borderRadius: "8px" }}
            />
          ) : (
            <DataTable
              ref={gridRef}
              columns={columnDefinition}
              rows={filteredData || []}
              tableHeight={tableHeight}
              suppressMovableColumns={true}
              suppressHorizontalScroll={false}
              suppressPaginationPanel={false}
              pagination={pagination}
              paginationPageSize={paginationPageSize}
              paginationPageSizeSelector={paginationPageSizeSelector}
              singleClickEdit={true}
              onCellValueChanged={onCellValueChanged}
              onGridReady={onGrid}
              rowSelection="multiple"
              getRowStyle={getRowStyle}
            />
          )}
        </Box>
      </div>
    </>
  );
}

export default ProfitCenterSelection;
